import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { AgentDrawerPanel } from "@/agents"
import { PersonaDrawerPanel, type RunPersonaSnapshot } from "@/persona"
import {
  TestCaseDrawerPanel,
  TestSetDrawerPanel,
  type TestCaseData,
} from "@/test-set"
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@coval/ui"

import {
  ConversationMetadataPanel,
  type ConversationMetadata,
} from "./ConversationMetadataPanel"

/**
 * A panel the drawer shows that is not one of the resources a run row points
 * at. The page above supplies it, so a surface can put its own content in the
 * shared slot without this module reaching back into that surface.
 */
export interface ResourceDrawerPanel {
  title: string
  content: React.ReactNode
}

/** Which resource the shared drawer is showing. */
type ResourceDrawerTarget =
  | { kind: "agent"; id: string }
  | { kind: "persona"; id: string; runPersona?: RunPersonaSnapshot | null }
  | {
      kind: "test-set"
      id: string
      testCase?: TestCaseData | null
      testSetVersionId?: string
      /**
       * Params this specific run actually executed with, for a parameterized
       * test case — distinct from the test case's current stored params,
       * which is all the panel would otherwise have to show.
       */
      executedTestCaseParams?: Record<string, string>
    }
  | {
      kind: "test-case"
      testSetId: string
      testCase: TestCaseData
      /** The test set version the run pinned, when it pinned one. */
      testSetVersionId?: string
    }
  | { kind: "conversation"; metadata: ConversationMetadata }
  | { kind: "panel"; id: string }

/**
 * The resources a newly selected row points at, keyed the way the drawer
 * shows them. A field left out means the row has none of that resource.
 */
export interface ResourceDrawerSelection {
  agentId?: string
  persona?: { id: string; runPersona?: RunPersonaSnapshot | null }
  testSet?: {
    id: string
    testCase?: TestCaseData | null
    testSetVersionId?: string
  }
  conversation?: ConversationMetadata
}

/**
 * What an open drawer shows once the page moves to another row: the same kind
 * of resource, taken from that row, or nothing when the row has none. Panels
 * belong to the page rather than to a row, so they stay.
 */
function followTarget(
  current: ResourceDrawerTarget,
  selection: ResourceDrawerSelection,
): ResourceDrawerTarget | null {
  const { agentId, persona, testSet, conversation } = selection
  switch (current.kind) {
    case "agent":
      return agentId ? { kind: "agent", id: agentId } : null
    case "persona":
      return persona ? { kind: "persona", ...persona } : null
    case "test-set":
      return testSet
        ? {
            kind: "test-set",
            ...testSet,
            executedTestCaseParams: testSet.testCase?.testCaseParams,
          }
        : null
    case "test-case":
      return testSet?.testCase
        ? {
            kind: "test-case",
            testSetId: testSet.id,
            testCase: testSet.testCase,
            testSetVersionId: testSet.testSetVersionId,
          }
        : null
    case "conversation":
      return conversation
        ? { kind: "conversation", metadata: conversation }
        : null
    case "panel":
      return current
  }
}

interface ResourceDrawerProps {
  target: ResourceDrawerTarget | null
  panels: Record<string, ResourceDrawerPanel>
  onClose: () => void
  onCloseAutoFocus: (event: Event) => void
}

const ResourceDrawer: React.FC<ResourceDrawerProps> = ({
  target,
  panels,
  onClose,
  onCloseAutoFocus,
}) => {
  // The drawer stays mounted through its slide-out, so it keeps rendering the
  // last resource it showed rather than emptying out mid-animation.
  const [shown, setShown] = useState(target)
  useEffect(() => {
    if (target) {
      setShown(target)
    }
  }, [target])

  const shownPanel = shown?.kind === "panel" ? panels[shown.id] : undefined

  return (
    <Drawer open={target != null} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent size="sm" onCloseAutoFocus={onCloseAutoFocus}>
        {shown?.kind === "agent" ? (
          <AgentDrawerPanel key={shown.id} agentId={shown.id} />
        ) : null}
        {shown?.kind === "persona" ? (
          <PersonaDrawerPanel
            key={shown.id}
            personaId={shown.id}
            runPersona={shown.runPersona}
          />
        ) : null}
        {shown?.kind === "test-set" ? (
          <TestSetDrawerPanel
            key={`${shown.id}:${shown.testSetVersionId ?? ""}:${shown.testCase?.id ?? ""}`}
            testSetId={shown.id}
            selectedTestCaseId={shown.testCase?.id}
            runTestCase={shown.testCase}
            testSetVersionId={shown.testSetVersionId}
            executedTestCaseParams={shown.executedTestCaseParams}
          />
        ) : null}
        {shown?.kind === "test-case" ? (
          <TestCaseDrawerPanel
            key={`${shown.testSetId}:${shown.testCase.id ?? ""}`}
            testSetId={shown.testSetId}
            testCase={shown.testCase}
            testSetVersionId={shown.testSetVersionId}
          />
        ) : null}
        {shown?.kind === "conversation" ? (
          <ConversationMetadataPanel metadata={shown.metadata} />
        ) : null}
        {shownPanel ? (
          <React.Fragment key={shown?.kind === "panel" ? shown.id : undefined}>
            <DrawerHeader>
              <DrawerTitle>{shownPanel.title}</DrawerTitle>
            </DrawerHeader>
            {shownPanel.content}
          </React.Fragment>
        ) : null}
      </DrawerContent>
    </Drawer>
  )
}

interface ResourceDrawerOpeners {
  openAgentDrawer: (id: string) => void
  openPersonaDrawer: (
    id: string,
    runPersona?: RunPersonaSnapshot | null,
  ) => void
  openTestSetDrawer: (
    id: string,
    testCase?: TestCaseData | null,
    opener?: HTMLElement | null,
    testSetVersionId?: string,
  ) => void
  openTestCaseDrawer: (
    testSetId: string,
    testCase: TestCaseData,
    testSetVersionId?: string,
  ) => void
  openConversationDrawer: (metadata: ConversationMetadata) => void
  openPanelDrawer: (id: string) => void
  /**
   * Moves an open drawer onto the row the page now shows, so it never goes on
   * describing the last one. A closed drawer stays closed.
   */
  followSelection: (selection: ResourceDrawerSelection) => void
  closeDrawer: (restoreFocus?: boolean) => void
}

interface ResourceDrawerControls extends ResourceDrawerOpeners {
  drawerElement: React.ReactNode
}

const ResourceDrawerContext = createContext<ResourceDrawerControls | null>(null)

function useResourceDrawerState(panels: Record<string, ResourceDrawerPanel>): {
  openers: ResourceDrawerOpeners
  drawerElement: React.ReactNode
} {
  const [target, setTarget] = useState<ResourceDrawerTarget | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)

  const openAgentDrawer = useCallback((id: string) => {
    openerRef.current = null
    setTarget({ kind: "agent", id })
  }, [])

  const openPersonaDrawer = useCallback(
    (id: string, runPersona?: RunPersonaSnapshot | null) => {
      openerRef.current = null
      setTarget({ kind: "persona", id, runPersona })
    },
    [],
  )

  const openTestSetDrawer = useCallback(
    (
      id: string,
      testCase?: TestCaseData | null,
      opener?: HTMLElement | null,
      testSetVersionId?: string,
    ) => {
      openerRef.current = opener ?? null
      setTarget({
        kind: "test-set",
        id,
        testCase,
        testSetVersionId,
        executedTestCaseParams: testCase?.testCaseParams,
      })
    },
    [],
  )

  const openTestCaseDrawer = useCallback(
    (testSetId: string, testCase: TestCaseData, testSetVersionId?: string) => {
      openerRef.current = null
      setTarget({ kind: "test-case", testSetId, testCase, testSetVersionId })
    },
    [],
  )

  const openConversationDrawer = useCallback(
    (metadata: ConversationMetadata) => {
      openerRef.current = null
      setTarget({ kind: "conversation", metadata })
    },
    [],
  )

  const openPanelDrawer = useCallback((id: string) => {
    openerRef.current = null
    setTarget({ kind: "panel", id })
  }, [])

  const followSelection = useCallback((selection: ResourceDrawerSelection) => {
    setTarget((current) =>
      current ? followTarget(current, selection) : current,
    )
  }, [])

  const closeDrawer = useCallback((restoreFocus = true) => {
    if (!restoreFocus) {
      openerRef.current = null
    }
    setTarget(null)
  }, [])

  const handleCloseAutoFocus = useCallback((event: Event) => {
    const opener = openerRef.current
    openerRef.current = null
    if (opener?.isConnected) {
      event.preventDefault()
      opener.focus()
    }
  }, [])

  // Held apart from the drawer element so a provider can hand the openers down
  // a whole tree without every consumer re-rendering as the slot changes.
  const openers = useMemo<ResourceDrawerOpeners>(
    () => ({
      openAgentDrawer,
      openPersonaDrawer,
      openTestSetDrawer,
      openTestCaseDrawer,
      openConversationDrawer,
      openPanelDrawer,
      followSelection,
      closeDrawer,
    }),
    [
      openAgentDrawer,
      openPersonaDrawer,
      openTestSetDrawer,
      openTestCaseDrawer,
      openConversationDrawer,
      openPanelDrawer,
      followSelection,
      closeDrawer,
    ],
  )

  const drawerElement = (
    <ResourceDrawer
      target={target}
      panels={panels}
      onClose={closeDrawer}
      onCloseAutoFocus={handleCloseAutoFocus}
    />
  )

  return { openers, drawerElement }
}

const NO_PANELS: Record<string, ResourceDrawerPanel> = {}

/**
 * Puts one drawer slot in reach of a whole page rather than of a single
 * component. Surfaces that open the drawer from more than one place — a header
 * card here, a toolbar button there — need those places to share a slot, or
 * each one animates its own panel in over the last. Panels supplied here fill
 * the same slot as the resources below.
 */
export const ResourceDrawerProvider: React.FC<{
  panels?: Record<string, ResourceDrawerPanel>
  children: React.ReactNode
}> = ({ panels = NO_PANELS, children }) => {
  const { openers, drawerElement } = useResourceDrawerState(panels)
  // Consumers below render nothing of their own; the slot lives here.
  const value = useMemo<ResourceDrawerControls>(
    () => ({ ...openers, drawerElement: null }),
    [openers],
  )

  return (
    <ResourceDrawerContext.Provider value={value}>
      {children}
      {drawerElement}
    </ResourceDrawerContext.Provider>
  )
}

/**
 * One drawer for every resource a run row can point at. Agents, personas,
 * test sets, test cases, and the conversation's own metadata share a single panel and a
 * single open slot, so opening one while another is showing replaces the
 * panel's contents instead of stacking a second panel over it; the panel's
 * close button is what dismisses it.
 *
 * Under a `ResourceDrawerProvider` this joins that page's slot and renders no
 * drawer of its own, so every opener on the page swaps one panel's contents.
 * Standalone, it owns a slot of its own and the caller renders `drawerElement`.
 */
export function useResourceDrawer(): ResourceDrawerControls {
  const shared = useContext(ResourceDrawerContext)
  const { openers, drawerElement } = useResourceDrawerState(NO_PANELS)
  return shared ?? { ...openers, drawerElement }
}
